import { useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { entryHours } from '../../../../supabase/functions/_shared/timeMath.ts';

// ============================================================================
// Tipos
// ============================================================================

export interface OrgTimeEntry {
  id: string;
  date: string; // yyyy-mm-dd
  hours: number;
  billable: boolean;
  user_id: string;
  project_id: string | null;
  project_name: string;
  client_id: number | null;
}

export interface OrgMember {
  auth_id: string;
  name: string;
  email: string;
}

// ============================================================================
// Período / datas
// ============================================================================

export const REPORT_PERIODS = [
  'Hoje',
  'Ontem',
  'Esta semana',
  'Semana anterior',
  'Últimos 7 dias',
  'Este mês',
  'Mês anterior',
  'Últimos 30 dias',
  'Últimos 90 dias',
  'Este ano',
  'Ano anterior',
  'Personalizado'
];

// Data local no formato yyyy-mm-dd (toISOString().split('T')[0] daria a data em UTC)
export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const createLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  return new Date(year, month - 1, day);
};

export const getDateRange = (
  period: string,
  customStart?: string,
  customEnd?: string
): { startDate: Date; endDate: Date } => {
  const endDate = new Date();
  const startDate = new Date();

  if (period === 'Personalizado' && customStart && customEnd) {
    // Datas inclusivas: o último dia vai até 23:59 (antes parava à meia-noite e perdia o dia)
    const start = createLocalDate(customStart) || startDate;
    const end = createLocalDate(customEnd) || endDate;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: end };
  }

  switch (period) {
    case 'Hoje':
      break;
    case 'Ontem':
      startDate.setDate(startDate.getDate() - 1);
      endDate.setDate(endDate.getDate() - 1);
      break;
    case 'Semana anterior':
      startDate.setDate(startDate.getDate() - startDate.getDay() - 7);
      endDate.setTime(startDate.getTime());
      endDate.setDate(startDate.getDate() + 6);
      break;
    case 'Mês anterior':
      startDate.setMonth(startDate.getMonth() - 1, 1);
      endDate.setDate(0);
      break;
    case 'Ano anterior':
      startDate.setFullYear(startDate.getFullYear() - 1, 0, 1);
      endDate.setFullYear(endDate.getFullYear() - 1, 11, 31);
      break;
    case 'Esta semana': {
      const dayOfWeek = startDate.getDay();
      startDate.setDate(startDate.getDate() - dayOfWeek);
      break;
    }
    case 'Últimos 7 dias':
      startDate.setDate(endDate.getDate() - (7 - 1));
      break;
    case 'Este mês':
      startDate.setDate(1);
      break;
    case 'Últimos 30 dias':
      startDate.setDate(endDate.getDate() - (30 - 1));
      break;
    case 'Últimos 90 dias':
      startDate.setDate(endDate.getDate() - (90 - 1));
      break;
    case 'Este ano':
      startDate.setMonth(0, 1);
      break;
    default:
      startDate.setDate(endDate.getDate() - (30 - 1));
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

/**
 * Período vizinho para as setas ‹ ›. Ano e mês que começam no dia 1 andam um
 * ano/mês inteiro; semana que começa no domingo anda uma semana inteira; o
 * resto anda o mesmo número de dias.
 */
export const shiftRange = (start: Date, end: Date, dir: 1 | -1): { start: string; end: string } => {
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const sameYear = s.getFullYear() === e.getFullYear();

  if (s.getMonth() === 0 && s.getDate() === 1 && sameYear && e.getMonth() > 0) {
    const year = s.getFullYear() + dir;
    return { start: `${year}-01-01`, end: `${year}-12-31` };
  }
  if (s.getDate() === 1 && sameYear && s.getMonth() === e.getMonth() && e.getDate() > 1) {
    const first = new Date(s.getFullYear(), s.getMonth() + dir, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    return { start: toLocalDateString(first), end: toLocalDateString(last) };
  }
  const days = Math.round((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  const step = s.getDay() === 0 && days > 1 && days <= 7 ? 7 : days;
  const newStart = new Date(s);
  newStart.setDate(s.getDate() + dir * step);
  const newEnd = new Date(newStart);
  newEnd.setDate(newStart.getDate() + step - 1);
  return { start: toLocalDateString(newStart), end: toLocalDateString(newEnd) };
};

export interface PeriodFilterState {
  period: string;
  setPeriod: (period: string) => void;
  isCustom: boolean;
  customStart: string;
  customEnd: string;
  setCustomStart: (value: string) => void;
  setCustomEnd: (value: string) => void;
  isValid: boolean;
  errorMessage: string;
  hasPendingChanges: boolean;
  applyCustom: () => void;
  shift: (dir: 1 | -1) => void;
  dateRange: { startDate: Date; endDate: Date };
}

/**
 * Estado padrão de seleção de período para os relatórios: presets rápidos
 * (Esta semana, Últimos 30 dias, ...) + intervalo personalizado com data
 * inicial/final e um passo explícito de "aplicar" — o mesmo comportamento
 * usado em todos os relatórios de Uso do Tempo.
 */
export function usePeriodFilter(defaultPeriod: string = 'Últimos 30 dias'): PeriodFilterState {
  const [period, setPeriodState] = useState(defaultPeriod);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedStart, setAppliedStart] = useState('');
  const [appliedEnd, setAppliedEnd] = useState('');

  const isCustom = period === 'Personalizado';

  const isValid = useMemo(() => {
    if (!isCustom || !customStart || !customEnd) return true;
    const start = createLocalDate(customStart);
    const end = createLocalDate(customEnd);
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    return start <= end;
  }, [isCustom, customStart, customEnd]);

  const errorMessage = useMemo(() => {
    if (!isCustom || !customStart || !customEnd) return '';
    const start = createLocalDate(customStart);
    const end = createLocalDate(customEnd);
    if (!start || isNaN(start.getTime())) return `Data inicial inválida: ${customStart}`;
    if (!end || isNaN(end.getTime())) return `Data final inválida: ${customEnd}`;
    if (start > end) {
      return `A data inicial (${start.toLocaleDateString('pt-BR')}) não pode ser posterior à data final (${end.toLocaleDateString('pt-BR')})`;
    }
    return '';
  }, [isCustom, customStart, customEnd]);

  const hasPendingChanges = isCustom && (customStart !== appliedStart || customEnd !== appliedEnd);

  const setPeriod = (value: string) => {
    setPeriodState(value);
    if (value === 'Personalizado' && !customStart && !customEnd) {
      const end = toLocalDateString(new Date());
      const start = toLocalDateString(new Date(new Date().setDate(new Date().getDate() - (30 - 1))));
      setCustomStart(start);
      setCustomEnd(end);
      setAppliedStart(start);
      setAppliedEnd(end);
    }
  };

  const applyCustom = () => {
    if (isCustom && customStart && customEnd && isValid) {
      setAppliedStart(customStart);
      setAppliedEnd(customEnd);
    }
  };

  const dateRange = useMemo(() => {
    if (isCustom) return getDateRange('Personalizado', appliedStart, appliedEnd);
    return getDateRange(period);
  }, [period, isCustom, appliedStart, appliedEnd]);

  const shift = (dir: 1 | -1) => {
    const next = shiftRange(dateRange.startDate, dateRange.endDate, dir);
    setPeriodState('Personalizado');
    setCustomStart(next.start);
    setCustomEnd(next.end);
    setAppliedStart(next.start);
    setAppliedEnd(next.end);
  };

  return {
    period,
    setPeriod,
    isCustom,
    customStart,
    customEnd,
    setCustomStart,
    setCustomEnd,
    isValid,
    errorMessage,
    hasPendingChanges,
    applyCustom,
    shift,
    dateRange
  };
}

// ============================================================================
// Busca de dados (Supabase)
// ============================================================================

const PAGE_SIZE = 1000;

/**
 * Busca todos os lançamentos de tempo finalizados da organização em um intervalo,
 * já com o nome do projeto e o id do cliente resolvidos.
 *
 * - `duration` é armazenado em MINUTOS (ver `timeEntryService.calculateDuration`).
 * - Paginado: o Supabase devolve no máximo 1000 linhas por requisição.
 * - `date` é a data LOCAL do início (o split da string UTC jogava o que começa
 *   depois das 21h no Brasil para o dia seguinte).
 * - Erro de consulta é lançado, para a tela mostrar a falha em vez de zeros.
 */
export async function fetchOrgTimeEntries(
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<OrgTimeEntry[]> {
  const rows: any[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('time_entries')
      .select(`
        id,
        start_time,
        end_time,
        duration,
        billable,
        user_id,
        project_id,
        projects!left (
          id,
          name,
          client_id
        )
      `)
      .eq('organization_id', organizationId)
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .not('end_time', 'is', null)
      .order('start_time', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error('Erro ao buscar lançamentos de tempo da organização:', error);
      throw error;
    }
    rows.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) break;
  }

  return rows.map((entry: any) => {
    const hours = entryHours(entry);

    return {
      id: entry.id,
      date: entry.start_time ? toLocalDateString(new Date(entry.start_time)) : '',
      hours,
      billable: entry.billable || false,
      user_id: entry.user_id,
      project_id: entry.project_id,
      project_name: entry.projects?.name || 'Sem Projeto',
      client_id: entry.projects?.client_id ?? null
    };
  });
}

/**
 * Membros da organização (organization_members) com nome e e-mail do perfil.
 * Antes vinha de user_profile.organization_id, que deixa de fora quem é membro
 * mas tem o perfil apontando para outra organização.
 */
export async function fetchOrgMembers(organizationId: string): Promise<OrgMember[]> {
  const { data: members, error } = await supabase
    .from('organization_members')
    .select('user_id')
    .eq('organization_id', organizationId);

  if (error) {
    console.error('Erro ao buscar membros da organização:', error);
    throw error;
  }

  const userIds = (members || []).map((m: any) => m.user_id).filter(Boolean);
  if (userIds.length === 0) return [];

  const { data: profiles, error: profilesError } = await supabase
    .from('user_profile')
    .select('auth_id, name, email')
    .in('auth_id', userIds);

  if (profilesError) {
    console.error('Erro ao buscar perfis dos membros:', profilesError);
    throw profilesError;
  }

  return userIds.map((id: string) => {
    const profile = (profiles || []).find((p: any) => p.auth_id === id);
    return {
      auth_id: id,
      name: profile?.name || profile?.email || 'Sem nome',
      email: profile?.email || ''
    };
  });
}

/** Busca os clientes da organização como um mapa id -> nome. */
export async function fetchOrgClientNames(organizationId: string): Promise<Map<number, string>> {
  const { data, error } = await supabase
    .from('clients')
    .select('id, name')
    .eq('organization_id', organizationId);

  if (error) {
    console.error('Erro ao buscar clientes da organização:', error);
    return new Map();
  }

  return new Map((data || []).map((c: any) => [c.id, c.name]));
}

// ============================================================================
// Formatação
// ============================================================================

export function formatHours(hours: number): string {
  if (!isFinite(hours) || hours < 0) hours = 0;
  let h = Math.floor(hours);
  let m = Math.round((hours - h) * 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return `${h}h ${m.toString().padStart(2, '0')}min`;
}

export function formatCurrencyBRL(value: number): string {
  if (!isFinite(value)) value = 0;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatPercentage(value: number, digits = 1): string {
  if (!isFinite(value)) return '0%';
  return `${value.toFixed(digits)}%`;
}

// Paleta das séries dos relatórios, na ordem fixa: coral do Nankin primeiro.
// Validada para daltonismo (dataviz validate_palette, modo claro).
export const REPORT_COLORS = ['#e54e40', '#2a78d6', '#1baf7a', '#eda100', '#6250d6', '#e87ba4', '#008300'];
// Da 8ª série em diante tudo fica cinza ("outros"), em vez de repetir cor
export const OTHER_COLOR = '#9a9a9a';
export const seriesColor = (index: number) => REPORT_COLORS[index] ?? OTHER_COLOR;

const AVATAR_COLORS = REPORT_COLORS;

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getAvatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
